import React, { useEffect, useRef, useState } from 'react';
import * as Menubar from '@radix-ui/react-menubar';
import { DotFilledIcon } from "@radix-ui/react-icons";
import { TabNode } from 'flexlayout-react';
import { Table, Column, HeaderCell, Cell } from 'rsuite-table';
import 'rsuite-table/dist/css/rsuite-table.css';

interface TableTabProps {
    tableIndex: string;
    tables: Map<string, any>;
    setTables: React.Dispatch<React.SetStateAction<Map<string, any>>>;
    node?: TabNode; // optional: used to listen to tab activation/resizes
}

const TableTab: React.FC<TableTabProps> = ({ tableIndex, tables, setTables, node }) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [tableId] = useState<string>(tableIndex);
    const [rows, setRows] = useState<any[]>([]);
    const [columns, setColumns] = useState<string[]>([]);
    const [width, setWidth] = useState<number | undefined>(undefined);

    // Normalize different table data shapes into an array of objects and column list
    useEffect(() => {
        const td = tables.get(tableId);
        if (!td) {
            setRows([]);
            setColumns([]);
            return;
        }

        // Possible shapes:
        // 1) Array of objects -> use keys
        // 2) { rows: Array<object>, columns?: string[] }
        // 3) Array of arrays -> columns may be provided in td.columns or create generic names
        // 4) Single object with data property
        let data: any[] = [];
        let cols: string[] = [];

        if (Array.isArray(td)) {
            // array could be objects or arrays
            if (td.length > 0 && Array.isArray(td[0])) {
                // array of arrays: treat first row as header (always)
                const arrays = td as any[][];
                const headerRow = arrays[0] || [];
                const colsFromHeader = headerRow.map((h: any, i: number) => String(h ?? `col${i + 1}`));
                cols = (td as any).columns && Array.isArray((td as any).columns) ? (td as any).columns : colsFromHeader;
                // data rows are the rest
                const body = arrays.slice(1);
                data = body.map((rowArr: any[]) => {
                    const obj: any = {};
                    cols.forEach((c, i) => { obj[c] = rowArr[i]; });
                    return obj;
                });
            } else if (td.length > 0 && typeof td[0] === 'object' && !Array.isArray(td[0])) {
                // array of objects -> use keys from first object
                data = td as any[];
                cols = Object.keys(data[0] || {});
            } else {
                data = td as any[];
                cols = data.length && typeof data[0] === 'object' ? Object.keys(data[0]) : [];
            }
        } else if (td && Array.isArray(td.rows)) {
            // td.rows could be array of arrays (with first row as header) or array of objects
            if (td.rows.length > 0 && Array.isArray(td.rows[0])) {
                const arrays = td.rows as any[][];
                const headerRow = arrays[0] || [];
                cols = td.columns && Array.isArray(td.columns) ? td.columns : headerRow.map((h: any, i: number) => String(h ?? `col${i + 1}`));
                const body = arrays.slice(1);
                data = body.map((rowArr: any[]) => {
                    const obj: any = {};
                    cols.forEach((c, i) => { obj[c] = rowArr[i]; });
                    return obj;
                });
            } else {
                data = td.rows;
                cols = td.columns && Array.isArray(td.columns) ? td.columns : (data.length ? Object.keys(data[0]) : []);
            }
        } else if (td && Array.isArray(td.data)) {
            if (td.data.length > 0 && Array.isArray(td.data[0])) {
                const arrays = td.data as any[][];
                const headerRow = arrays[0] || [];
                cols = td.columns && Array.isArray(td.columns) ? td.columns : headerRow.map((h: any, i: number) => String(h ?? `col${i + 1}`));
                const body = arrays.slice(1);
                data = body.map((rowArr: any[]) => {
                    const obj: any = {};
                    cols.forEach((c, i) => { obj[c] = rowArr[i]; });
                    return obj;
                });
            } else {
                data = td.data;
                cols = td.columns && Array.isArray(td.columns) ? td.columns : (data.length ? Object.keys(data[0]) : []);
            }
        } else if (typeof td === 'object') {
            // maybe it's a single object representing a row or keyed object with columns
            if (td.rows && Array.isArray(td.rows)) {
                if (td.rows.length > 0 && Array.isArray(td.rows[0])) {
                    const arrays = td.rows as any[][];
                    const headerRow = arrays[0] || [];
                    cols = td.columns && Array.isArray(td.columns) ? td.columns : headerRow.map((h: any, i: number) => String(h ?? `col${i + 1}`));
                    const body = arrays.slice(1);
                    data = body.map((rowArr: any[]) => {
                        const obj: any = {};
                        cols.forEach((c, i) => { obj[c] = rowArr[i]; });
                        return obj;
                    });
                } else {
                    data = td.rows;
                    cols = td.columns && Array.isArray(td.columns) ? td.columns : (data.length ? Object.keys(data[0]) : []);
                }
            } else {
                // treat as single-row object
                data = [td];
                cols = Object.keys(td);
            }
        }

        setRows(data || []);
        setColumns(cols || []);
    }, [tables, tableId]);

    // Export helpers
    const download = (content: string, filename: string, mime = 'text/plain') => {
        const blob = new Blob([content], { type: mime + ';charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
    };

    const exportCSV = () => {
        if (!rows || rows.length === 0) return;
        const cols = columns.length ? columns : Object.keys(rows[0] || {});
        const lines: string[] = [];
        lines.push(cols.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','));
        rows.forEach(r => {
            const row = cols.map(c => {
                const v = r[c];
                if (v === undefined || v === null) return '';
                const s = String(v);
                // escape quotes
                return `"${s.replace(/"/g, '""')}"`;
            });
            lines.push(row.join(','));
        });
        download(lines.join('\n'), `${tableId || 'table'}.csv`, 'text/csv');
    };

    const exportCode = (format: 'python' | 'matlab') => {
        if (!rows || rows.length === 0) return;
        const cols = columns.length ? columns : Object.keys(rows[0] || {});

        if (format === 'python') {
            // create Python code that builds a pandas DataFrame
            let code = 'import pandas as pd\n\n';
            // Build a dict of lists
            cols.forEach(col => {
                const arr = rows.map(r => r[col] === undefined || r[col] === null ? null : r[col]);
                const pyArr = JSON.stringify(arr).replace(/null/g, 'None');
                // safe column name
                const safe = String(col).replace(/'/g, "\\'");
                code += `${safe.replace(/\s+/g, '_')} = ${pyArr}\n`;
            });
            const dictEntries = cols.map(col => {
                const safe = String(col).replace(/\s+/g, '_');
                return `'${String(col).replace(/'/g, "\\'")}' : ${safe}`;
            }).join(', ');
            code += `\ndf = pd.DataFrame({ ${dictEntries} })\nprint(df)\n`;
            download(code, `${tableId || 'table'}.py`, 'text/x-python');
        } else {
            // Matlab: build cell array or matrix depending on types (fallback to cellarray)
            let code = '';
            // check if all values are numeric per column
            const isNumericCol = (col: string) => rows.every(r => r[col] === null || r[col] === undefined || typeof r[col] === 'number' || !isNaN(Number(r[col])));
            const numericCols = cols.map(c => isNumericCol(c));

            // If all columns numeric, create matrix
            if (numericCols.every(Boolean)) {
                const matRows = rows.map(r => cols.map(c => r[c] === null || r[c] === undefined ? 'NaN' : Number(r[c])));
                code += 'data = [\n';
                matRows.forEach(row => {
                    code += '  ' + row.join(' ') + ';\n';
                });
                code += '];\n';
                code += '\nfigure; plot(data);\n';
            } else {
                // create cell arrays per column
                cols.forEach((c, idx) => {
                    const arr = rows.map(r => r[c] === undefined || r[c] === null ? "''" : (typeof r[c] === 'number' ? String(r[c]) : `'${String(r[c]).replace(/'/g, "''")}'`));
                    code += `${c.replace(/\s+/g, '_')} = { ${arr.join(', ')} }';\n`;
                });
                code += '\n% add your plotting code here\n';
            }
            download(code, `${tableId || 'table'}.m`, 'text/x-matlab');
        }
    };

    // Resize handling: observe container and update width state to force reflow
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const handleResize = () => {
            setWidth(el.clientWidth);
        };

        if ((window as any).ResizeObserver) {
            const ro = new (window as any).ResizeObserver(() => handleResize());
            ro.observe(el);

            const mo = new (window as any).MutationObserver(() => handleResize());
            mo.observe(el, { attributes: true, attributeFilter: ['style', 'class'] });

            // Tab activation hook
            const handleTabActivation = () => {
                setTimeout(handleResize, 0);
            };
            if (node) {
                node.setEventListener('resize', handleTabActivation);
            }

            // initial
            handleResize();

            return () => {
                ro.disconnect();
                mo.disconnect();
                if (node) {
                    node.setEventListener('resize', () => {});
                }
            };
        } else {
            // fallback: window resize
            window.addEventListener('resize', handleResize);
            handleResize();
            return () => window.removeEventListener('resize', handleResize);
        }
    }, [containerRef, node]);

    // Compute max column width: allow tables metadata to provide maxColumnWidth or maxWidth
    const tableMeta = tables.get(tableId) || {};
    const maxColWidth = (typeof tableMeta === 'object' && (tableMeta.maxColumnWidth ?? tableMeta.maxWidth)) || 300;

    return (
        <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Menubar.Root className="menubar" style={{ position: 'relative', zIndex: 10, padding: 6 }}>
                <Menubar.Menu>
                    <Menubar.Trigger className="menubar-trigger">Export</Menubar.Trigger>
                    <Menubar.Content className="menubar-content" sideOffset={1} align="start" style={{ zIndex: 10 }}>
                        <Menubar.Item className="menubar-item" onSelect={exportCSV}>CSV</Menubar.Item>
                        <Menubar.Item className="menubar-item" onSelect={() => exportCode('python')}>Python</Menubar.Item>
                        <Menubar.Item className="menubar-item" onSelect={() => exportCode('matlab')}>Matlab</Menubar.Item>
                    </Menubar.Content>
                </Menubar.Menu>
            </Menubar.Root>

            <div ref={containerRef} style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 8 }}>
                {rows.length === 0 ? (
                    <div style={{ padding: 12, color: '#666' }}>No data</div>
                ) : (
                    // Note: virtualized is disabled so row heights can expand for multiline content
                    <Table wordWrap="break-word" autoHeight={false} data={rows} affixHeader={true} virtualized={false} height={Math.max(200, (containerRef.current?.clientHeight || 300) - 16) }>
                        {columns.map((col) => (
                            <Column key={col} flexGrow={1} fixed={false} align="left" verticalAlign="middle">
                                {/* Allow header to wrap */}
                                <HeaderCell style={{ whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'break-word', maxWidth: typeof maxColWidth === 'number' ? `${maxColWidth}px` : maxColWidth }}>{col}</HeaderCell>
                                {/* Render function to allow multiline wrapping in cell content */}
                                <Cell>
                                    {(rowData: any) => {
                                        const v = rowData ? rowData[col] : '';
                                        return (
                                            <div style={{
                                                whiteSpace: 'normal',
                                                overflowWrap: 'break-word',
                                                wordBreak: 'break-word',
                                                display: 'block',
                                                maxWidth: typeof maxColWidth === 'number' ? `${maxColWidth}px` : maxColWidth,
                                                // ensure long words are broken and don't overflow the cell
                                            }}>{v}</div>
                                        );
                                    }}
                                </Cell>
                            </Column>
                        ))}
                    </Table>
                )}
            </div>
        </div>
    );
};

export default TableTab;